/* eslint-disable vue/valid-v-slot */
<template>
  <div class="container pa-4">
    <div class="d-flex justify-space-between align-center mb-6">
      <span class="text-h5">Workshop Results</span>
    </div>

    <v-card class="result-table-card">
      <v-data-table 
        :headers="computedHeaders" 
        :items="tableData" 
        :item-key="'id'"
        :sort-by="sortBy"
        :sort-desc="sortDesc"
        :search="filterText"
        class="result-table no-vertical-borders top-align-all"
        density="comfortable"
        :cell-props="cellPropsFunction"
        :header-props="{ class: 'text-start align-start', style: 'padding-top: 24px !important; padding-bottom: 16px !important;' }"
      >
        <!-- eslint-disable vue/valid-v-slot -->
        <template #item.qualityAspect="{ item }">
          <div class="font-weight-bold">
            {{ item.qualityAspect }}
          </div>
        </template>

        <template #item.voteWhereWantToBe="{ item }">
          <div class="gap-2">
            <v-chip color="primary">
              {{ item.voteWhereWantToBe }}
            </v-chip>
            <v-chip color="error">
              {{ item.voteWhereWeAre }}
            </v-chip>
          </div>
        </template>

        <template #item.recommendations="{ item }">
          <div class="ideas-container" :style="{ verticalAlign: 'top' }">
            <v-expansion-panels variant="accordion">
              <v-expansion-panel :key="item.id + '_' + item.displayMode">
                <v-expansion-panel-title class="d-flex align-center">
                  <v-icon icon="mdi-lightbulb" color="warning" class="mr-2" />
                  <strong>Improvements</strong>
                  <v-progress-circular
                    v-if="item.llmLoading && llmFunctionalityEnabled"
                    indeterminate
                    size="18"
                    color="primary"
                    class="ml-2"
                  />
                  <span v-if="!llmFunctionalityEnabled" class="ml-2 text-grey">(LLM disabled)</span>
                </v-expansion-panel-title>
                <v-expansion-panel-text>
                  <v-dialog v-model="item.llmErrorDialogOpen" max-width="400" persistent>
                    <v-card>
                      <v-card-title class="text-error">LLM Error</v-card-title>
                      <v-card-text>{{ item.llmError }}</v-card-text>
                      <v-card-actions>
                        <v-btn color="primary" text @click="item.llmErrorDialogOpen = false">Close</v-btn>
                      </v-card-actions>
                    </v-card>
                  </v-dialog>
                  <template v-if="llmFunctionalityEnabled">
                    <template v-if="item.displayMode === 'llm'">
                      <div class="ideas" v-for="(idea, ideaIndex) in truncated(item.llmRecs)" :key="'llm_imp_'+ideaIndex">
                        <div v-html="idea"></div>
                      </div>
                    </template>
                    <template v-else>
                      <div v-if="item.recommendations && item.recommendations.length > 0">
                        <div class="ideas" v-for="(idea, ideaIndex) in truncated(item.recommendations)" :key="'static_imp_'+ideaIndex">
                          <div v-html="idea"></div>
                        </div>
                      </div>
                      <div v-else class="text-grey">No improvements available for this aspect.</div>
                      <div v-if="item.llmLoading" class="text-primary mt-1">Loading LLM improvements...</div>
                    </template>
                  </template>
                  <template v-else>
                    <div v-if="item.recommendations && item.recommendations.length > 0">
                      <div class="ideas" v-for="(idea, ideaIndex) in truncated(item.recommendations)" :key="'static_imp_'+ideaIndex">
                        <div v-html="idea"></div>
                      </div>
                    </div>
                    <div v-else class="text-grey">No improvements available for this aspect.</div>
                  </template>
                </v-expansion-panel-text>
              </v-expansion-panel>
            </v-expansion-panels>
          </div>
        </template>

        <template #item.measures="{ item }">
          <div class="measures-container">
            <v-text-field
              v-model="item.newmeasures"
              placeholder="Add New Task"
              variant="outlined"
              hide-details
              class="task-input"
              append-inner-icon="mdi-plus"
              @click:append-inner="addNewTask(item)"
              @keyup.enter="addNewTask(item)"
              density="compact"
              bg-color="white"
              single-line
            />
            
            <div v-if="filterTasksByQualityAspect(item.qualityAspect).length > 0" class="task-list mt-2">
              <div
                v-for="(task, index) in filterTasksByQualityAspect(item.qualityAspect)"
                :key="index"
                class="task-item"
              >
                <span class="task-summary">
                  <v-icon icon="mdi-check-circle" color="success" size="x-small" class="mr-2" />
                  {{ task.summary }}
                </span>
                <v-icon 
                  icon="mdi-delete" 
                  size="x-small" 
                  color="error" 
                  class="delete-icon"
                  @click="confirmDeleteTask(item, task)"
                />
              </div>
            </div>
          </div>
        </template>
        <!-- eslint-enable vue/valid-v-slot -->
      </v-data-table>
    </v-card>
  </div>
</template>

<script>
import {addTask, loadTasks, saveWorkPlan, deleteTask} from "@/workshop/workshopFunctions";
import { getDatabase, ref, onValue, get, set } from "firebase/database";
import segmentRiskData from '@/data/segmentRisk.json';
import { processAllVotedAspects } from '@/utils/riskScore';

export default {
  name: "resultTable",
  props: {
    customerId: {
      type: String,
      required: true
    },
    riskAssessmentEnabled: {
      type: Boolean,
      default: true
    },
    workshopId: {
      type: String,
      required: true
    },
    workshopData: {
      type: Object,
      required: true
    },
    selection: {
      type: Array,
      default: () => []
    },
    llmFunctionalityEnabled: {
      type: Boolean,
      required: true
    },
    projectDescription: {
      type: String,
      default: ''
    }
  },
  data() {
    return {
      tasks: [],
      localWorkshopData: null,
      // dedup in-flight LLM fetches per aspect
      inFlight: {},
      tableHeaders: [
        { title: 'Quality Aspect', align: 'start', key: 'qualityAspect', width: '14%' },
        { title: 'Team Rating', align: 'start', key: 'voteWhereWantToBe', width: '8%' },
        { title: 'Risk Level', align: 'start', key: 'riskLevel', width: '10%' },
        { title: 'Risk Score', align: 'start', key: 'riskScore', width: '8%' },
        { title: 'Ideas', align: 'start', key: 'recommendations', width: '30%' },
        { title: 'Tasks', align: 'start', key: 'measures', width: '30%' }
      ],
      tableData: [],
      ideaCategories: {
        improve: ['improve', 'enhancement', 'optimization'],
        verify: ['verify', 'validation', 'check'],
        test: ['test', 'testing', 'assessment']
      },
      sortBy: [{ key: 'riskScore', order: 'desc' }],
      sortDesc: true,
      filterText: '',
    }
  },
  watch: {
    selection: {
      handler() {
        this.loadData();
      },
      deep: true
    },
    workshopData: {
      handler(newVal) {
        this.localWorkshopData = JSON.parse(JSON.stringify(newVal));
        this.loadData();
      },
      immediate: true,
      deep: true
    },
  },
  computed: {
    computedHeaders() {
      const enabled = !!this.riskAssessmentEnabled;
      if (enabled) return this.tableHeaders;
      // Clone headers and remove risk columns
      const headers = this.tableHeaders
        .filter(h => h.key !== 'riskLevel' && h.key !== 'riskScore')
        .map(h => ({ ...h }));
      // Re-balance widths to consume full table when risk is disabled
      const qa = headers.find(h => h.key === 'qualityAspect');
      const tr = headers.find(h => h.key === 'voteWhereWantToBe');
      const ideas = headers.find(h => h.key === 'recommendations');
      const tasks = headers.find(h => h.key === 'measures');
      if (qa) qa.width = '16%';
      if (tr) tr.width = '14%';
      if (ideas) ideas.width = '50%';
      if (tasks) tasks.width = '20%';
      return headers;
    }
  },
  mounted() {
    this.loadInitialData();
    
    // Listen for real-time updates to tasks
    const db = getDatabase();
    const tasksRef = ref(db, `customers/${this.customerId}/workshop/${this.workshopId}/tasks`);
    onValue(tasksRef, (snapshot) => {
      const tasksData = snapshot.val();
      this.tasks = tasksData ? Object.values(tasksData) : [];
      console.log('Tasks updated from Firebase:', this.tasks);
      this.loadData(); // Reload data when tasks change
    });
  },
  methods: {
    cellPropsFunction() {
      return {
        class: 'text-start align-start vertical-align-top',
        style: 'vertical-align: top !important; align-items: flex-start !important; padding-top: 24px !important; padding-bottom: 16px !important;'
      };
    },
    // ---- RTDB cache helpers (cost-saving; avoid re-querying the LLM) ----
    aspectKey(aspect) {
      const id = String(aspect?.id || aspect)?.replace(/\./g, '_');
      return id;
    },
    async readRecsCache(aspect) {
      try {
        const db = getDatabase();
        const key = this.aspectKey(aspect);
        const p = ref(db, `customers/${this.customerId}/workshop/${this.workshopId}/llm/recs/${key}`);
        const snap = await get(p);
        return snap.exists() ? (snap.val() || []) : null;
      } catch {
        return null;
      }
    },
    async writeRecsCache(aspect, recs) {
      try {
        const db = getDatabase();
        const key = this.aspectKey(aspect);
        const p = ref(db, `customers/${this.customerId}/workshop/${this.workshopId}/llm/recs/${key}`);
        await set(p, Array.isArray(recs) ? recs : []);
      } catch {
        // best-effort
      }
    },
    async loadInitialData() {
      try {
        // Load tasks
        const tasks = await loadTasks(this.customerId, this.workshopId);
        this.tasks = tasks || [];
        console.log('Initial tasks loaded:', this.tasks);
        
        // Initialize local workshop data
        this.localWorkshopData = JSON.parse(JSON.stringify(this.workshopData));
        this.loadData();
      } catch (error) {
        console.error('Error loading initial data:', error);
      }
    },
    async addNewTask(item) {
      if (!item.newmeasures?.trim()) return;

      try {
        const newTask = {
          summary: item.newmeasures.trim(),
          qualityAspect: item.qualityAspect,
          assignee: "",
          parentIssue: "",
          dueDate: new Date().toISOString(),
          originalEffort: "",
          description: "",
          trackerKey: null,
          labels: [],
          dirty: {
            summary: false,
            assignee: false,
            parentIssue: false,
            dueDate: false,
            originalEffort: false,
            description: false,
            qualityAspect: false,
          },
        };

        console.log('Adding new task:', newTask);
        
        // Add task to Firebase
        const taskId = await addTask(this.customerId, this.workshopId, newTask);
        console.log('Task added with ID:', taskId);
        
        // Clear the input field
        item.newmeasures = '';
        
        // Reload tasks
        const updatedTasks = await loadTasks(this.customerId, this.workshopId);
        this.tasks = updatedTasks || [];
        console.log('Updated tasks:', this.tasks);
      } catch (error) {
        console.error('Error adding task:', error);
      }
    },
    
    filterTasksByQualityAspect(qualityAspect) {
      if (!Array.isArray(this.tasks)) return [];
      return this.tasks.filter(task => task.qualityAspect === qualityAspect);
    },
    
    async saveWorkshop() {
      try {
        // Create a new array from tableData with only necessary information
        const newData = this.tableData.map(item => ({
          id: item.id,
          measures: Array.isArray(item.measures) ? item.measures.filter(Boolean) : []
        }));

        await saveWorkPlan(this.customerId, this.workshopId, newData);
      } catch (error) {
        console.error('Error saving workshop:', error);
      }
    },
    next() {
      this.saveWorkshop().then(() => {
        this.$emit('next');
      });
    },
    back() {
      this.$emit('back');
    },
    getIdeasByCategory(ideas, category) {
      if (!ideas) return [];
      
      const keywords = this.ideaCategories[category];
      return ideas.filter(idea => {
        const lowerIdea = idea.toLowerCase();
        return keywords.some(keyword => lowerIdea.includes(keyword));
      });
    },
    confirmDeleteTask(item, task) {
      const hasText = (task.summary || '').trim().length > 0 || (task.description || '').trim().length > 0;
      const ok = hasText
        ? window.confirm('Are you sure you want to delete this task? This action cannot be undone.')
        : window.confirm('Delete empty task?');
      if (!ok) return;
      this.deleteTask(item, task);
    },
    async deleteTask(item, task) {
      const index = this.tasks.findIndex(t => t.summary === task.summary && t.qualityAspect === task.qualityAspect);
      if (index !== -1) {
        this.tasks.splice(index, 1);
        await deleteTask(this.customerId, this.workshopId, task);
      }
    },
    truncated(arr) {
      if (!Array.isArray(arr)) return [];
      return arr.map((s) => {
        const t = String(s || '');
        return t.length > 300 ? `${t.slice(0, 300)}…` : t;
      });
    },
    loadData() {
      if (!this.localWorkshopData || !this.selection) return;

      // Build processed risk aspects from workshop results to align with Risk Assessment
      const results = this.localWorkshopData.results || {};
      const processedAspects = processAllVotedAspects(results, segmentRiskData);
      const byId = processedAspects.reduce((acc, a) => { acc[String(a.id)] = a; return acc; }, {});

      const data = [];
      for (const topicId of this.selection) {
        const stringTopicId = topicId.toString();
        const normalizedId = stringTopicId.replace(/\./g, '_');
        const result = this.localWorkshopData.results ? this.localWorkshopData.results[stringTopicId] : null;
        const riskData = byId[stringTopicId] || {};
        
        if (result) {
          const topic = this.findTopicById(stringTopicId);
          if (topic) {
            const recommendations = this.generateRecommendations(topic);
            data.push({
              id: normalizedId,
              qualityAspect: topic.text.en.name,
              voteWhereWantToBe: result.voteWhereWantToBe,
              voteWhereWeAre: result.voteWhereWeAre,
              riskLevel: riskData.riskLevel || 'Low',
              riskScore: Number(riskData.compositeScore || 0).toFixed(2),
              recommendations: recommendations,
              aspect: topic,
              llmRecs: [],
              llmError: '',
              llmErrorDialogOpen: false,
              llmLoading: !!this.llmFunctionalityEnabled,
              displayMode: 'fallback',
            });
          }
        }
      }
      this.tableData = data;
      // Pre-fetch LLM recommendations in background without extra clicks
      if (this.llmFunctionalityEnabled) {
        this.$nextTick(() => {
          this.tableData.forEach(row => {
            this.fetchLLMRecommendations(row);
          });
        });
      }
    },
    getRiskLabel(level) {
      const labels = {
        1: 'Low Risk',
        2: 'Medium Risk',
        3: 'High Risk'
      };
      return labels[level] || 'Unknown';
    },
    getRiskColor(level) {
      const colors = {
        1: 'success',
        2: 'warning',
        3: 'error'
      };
      return colors[level] || 'grey';
    },
    generateRecommendations(topic) {
      if (!topic) return [];
      const suggestions = require('../data/segmentSugesstion.json');
      const normalizedId = String(topic.id).replace(/\./g, '_');
      const topicSuggestions = suggestions.find(s => s.id === topic.id || s.id === normalizedId);
      if (topicSuggestions && topicSuggestions.text.en.recommendations) {
        console.log('Found static recommendations for', topic.id, topicSuggestions.text.en.recommendations);
        return topicSuggestions.text.en.recommendations;
      }
      console.log('No static recommendations for', topic.id);
      return [];
    },
    getMeasuresForTopic(topicId) {
      if (!this.tasks) return [];
      return this.tasks.filter(task => task.qualityAspect === topicId).map(task => task.summary);
    },
    findTopicById(topicId) {
      const segmentData = require('../data/segment.json');
      for (const category of segmentData) {
        for (const subcategory of category.children) {
          if (subcategory.id === topicId) {
            return subcategory;
          }
        }
      }
      return null;
    },
    showImprovementsClicked(item) {
      item.llmError = '';
      item.llmRecs = [];
      item.displayMode = 'button';
      this.fetchLLMRecommendations(item);
    },
    async fetchLLMRecommendations(item) {
      const row = this.tableData.find(row => row.id === item.id);
      if (!row) return;
      if (row.llmRecs.length > 0) return;

      // dedupe concurrent calls
      const inflightKey = `recs:${this.aspectKey(row.aspect)}`;
      if (this.inFlight[inflightKey]) {
        await this.inFlight[inflightKey];
        return;
      }

      row.llmLoading = true;
      row.llmError = '';
      row.displayMode = 'button';
      try {
        // 1) RTDB cache first
        const cached = await this.readRecsCache(row.aspect);
        if (Array.isArray(cached) && cached.length) {
          row.llmRecs = cached;
          row.displayMode = 'llm';
        } else {
          // 2) Remote call (Azure Worker) with tight timeout
          const base = import.meta.env.VITE_LLM_BASE || '';
          if (!base) throw new Error('no_llm_base');
          const controller = new AbortController();
          const t = setTimeout(() => controller.abort(), 6000);
          const promise = fetch(`${base}/segment-recommendations`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              name: item.aspect.text?.en?.name || item.aspect.qualityAspect || item.aspect.name,
              votingResults: {
                voteWhereWantToBe: item.voteWhereWantToBe,
                voteWhereWeAre: item.voteWhereWeAre
              },
              riskScore: item.riskScore,
              projectDescription: this.projectDescription || ''
            }),
            signal: controller.signal
          }).then(async (res) => {
            clearTimeout(t);
            if (!res.ok) throw new Error('llm_failed');
            const data = await res.json();
            const recs = Array.isArray(data?.recommendations) ? data.recommendations : [];
            if (recs.length) await this.writeRecsCache(row.aspect, recs);
            return recs;
          });
          this.inFlight[inflightKey] = promise;
          const recs = await promise.finally(() => { delete this.inFlight[inflightKey]; });
          if (recs.length) {
            row.llmRecs = recs;
            row.displayMode = 'llm';
          } else {
            row.llmRecs = [];
            row.displayMode = 'fallback';
          }
        }
      } catch (e) {
        row.llmError = 'LLM unavailable: ' + e;
        row.llmRecs = [];
        row.displayMode = 'fallback';
        // Do not open blocking dialog in production; keep static content
        row.llmErrorDialogOpen = false;
      }
      row.llmLoading = false;
      this.$forceUpdate();
    },
  }
};
</script>

<style>
/* CACHE BUST VERSION 2025-11-20-v5-INLINE-STYLES */

/* GLOBAL TOP ALIGNMENT - ABSOLUTE PRIORITY */
.top-align-all * {
  vertical-align: top !important;
}

.container {
  max-width: 1400px;
  margin: 0 auto;
}

.result-table-card {
  background: white;
  border-radius: 8px;
  overflow: hidden;
  /* NEW VERSION MARKER - if you see this comment in dev tools, new version loaded */
  border-top: 3px solid transparent; /* invisible marker */
}

.result-table {
  width: 100%;
}

.result-table :deep(.v-table__wrapper) {
  width: 100%;
}

.result-table :deep(.v-table__wrapper table) {
  table-layout: fixed;
  width: 100%;
}

/* NUCLEAR OPTION: Override ALL Vuetify centering */
.result-table :deep(.v-data-table-header th),
.result-table :deep(.v-data-table-header th .v-data-table-header__content),
.result-table :deep(.v-data-table__td),
.result-table :deep(.v-data-table__th) {
  text-align: left !important;
  justify-content: flex-start !important;
  align-items: flex-start !important;
}

.result-table :deep(.v-data-table__td > *),
.result-table :deep(.v-data-table__th > *) {
  justify-content: flex-start !important;
  align-items: flex-start !important;
  align-self: flex-start !important;
}

/* CRITICAL: Force ALL table cells to align content to TOP even when row expands */
.result-table :deep(tr) {
  vertical-align: top !important;
}
.result-table :deep(td),
.result-table :deep(th) {
  vertical-align: top !important;
  padding-top: 24px !important;
  padding-bottom: 16px !important;
}

/* Ensure content wrappers also stay at top */
.result-table :deep(.v-data-table__td),
.result-table :deep(.v-data-table__th) {
  vertical-align: top !important;
  align-content: flex-start !important;
}

/* INLINE STYLE OVERRIDE - HIGHEST SPECIFICITY */
.result-table :deep(tbody td[style]),
.result-table :deep(thead th[style]) {
  vertical-align: top !important;
}

/* Override any Vuetify inline styles */
.vertical-align-top {
  vertical-align: top !important;
}

/* ULTRA-AGGRESSIVE: REMOVE ALL VERTICAL BORDERS */
.no-vertical-borders,
.no-vertical-borders *,
.no-vertical-borders :deep(*),
.result-table,
.result-table *,
.result-table :deep(*) {
  border-left: 0 !important;
  border-right: 0 !important;
}

/* REMOVE ALL VERTICAL BORDERS FROM TABLE */
.result-table :deep(thead th),
.result-table :deep(tbody td),
.result-table :deep(tbody tr),
.result-table :deep(thead tr) {
  border-left: 0 !important;
  border-right: 0 !important;
  vertical-align: top !important;
}

/* TOP-LEFT ALIGNMENT FOR ALL CELLS */
.result-table :deep(tbody > tr > td) {
  vertical-align: top !important;
  text-align: left !important;
  border-left: 0 !important;
  border-right: 0 !important;
}

.result-table :deep(thead th) {
  vertical-align: top !important;
  text-align: left !important;
  border-left: 0 !important;
  border-right: 0 !important;
}

/* Force top-left on all child elements */
.result-table :deep(tbody td),
.result-table :deep(tbody td *),
.result-table :deep(thead th),
.result-table :deep(thead th *) {
  text-align: left !important;
  vertical-align: top !important;
  align-items: flex-start !important;
  justify-content: flex-start !important;
  border-left: 0 !important;
  border-right: 0 !important;
}

/* Fix v-chip alignment (they default to centered) */
.result-table :deep(.v-chip) {
  align-self: flex-start !important;
  margin: 2px !important;
  vertical-align: top !important;
}

.result-table :deep(.gap-2) {
  display: flex !important;
  flex-direction: row !important;
  align-items: flex-start !important;
  justify-content: flex-start !important;
  align-self: flex-start !important;
}

/* Prevent flex items from centering when parent grows */
.result-table :deep(.v-data-table__td) {
  display: table-cell !important;
  vertical-align: top !important;
}

.result-table :deep(.v-data-table__td .v-data-table__td-content) {
  display: block !important;
  vertical-align: top !important;
  align-self: flex-start !important;
}
.result-table :deep(tbody > tr > td:last-child) {
  width: auto !important;
  max-width: none !important;
  border-left: none !important;
}
.result-table :deep(tbody > tr > td:last-child > .v-data-table__td-content) {
  display: block !important;
  width: 100% !important;
}
.result-table :deep(tbody > tr > td:last-child .task-list) {
  width: 100% !important;
}

/* Ensure Tasks column has reasonable width (header + body) */
.result-table :deep(thead th:last-child),
.result-table :deep(tbody td:last-child) {
  min-width: 450px !important;
  width: 450px !important;
  max-width: 450px !important;
  padding-left: 16px !important;
  padding-right: 16px !important;
  box-sizing: border-box !important;
}
.result-table :deep(tbody > tr > td > .v-data-table__td-content) {
  display: flex !important;
  align-items: flex-start !important;
  justify-content: flex-start !important;
  width: 100% !important;
  text-align: left !important;
}
.result-table :deep(tbody > tr > td > .v-data-table__td-content > .v-expansion-panels),
.result-table :deep(tbody > tr > td > .v-data-table__td-content > .v-expansion-panels > .v-expansion-panel) {
  width: 100% !important;
}
.result-table :deep(tbody > tr > td .v-expansion-panel-title),
.result-table :deep(tbody > tr > td .v-expansion-panel-text) {
  align-items: flex-start !important;
}
.result-table :deep(.v-expansion-panel-text__wrapper) {
  align-items: flex-start !important;
  text-align: left !important;
}
.result-table :deep(tbody > tr > td > .v-data-table__td-content > *) {
  align-self: flex-start !important;
}

.result-table :deep(.v-data-table__td) {
  vertical-align: top !important;
}
.result-table :deep(.v-data-table__td .v-data-table__td-content) {
  align-items: flex-start !important;
  justify-content: flex-start !important;
  display: flex !important;
}
.result-table :deep(.v-data-table__td .v-data-table__td-content > *) {
  align-self: flex-start !important;
}

/* Aggressively remove ANY vertical borders anywhere in the table */
.result-table :deep(*) {
  border-left: 0 !important;
  border-right: 0 !important;
}
.result-table :deep(tbody > tr > td:last-child *),
.result-table :deep(tbody > tr > td:last-child)::before,
.result-table :deep(tbody > tr > td:last-child)::after {
  border-left: 0 !important;
  border-right: 0 !important;
}

/* Remove Vuetify's default table borders */
.result-table :deep(.v-table) {
  border: none !important;
}
.result-table :deep(.v-table__wrapper) {
  border: none !important;
}
.result-table :deep(.v-table .v-table__wrapper > table > tbody > tr > td),
.result-table :deep(.v-table .v-table__wrapper > table > thead > tr > th) {
  border-left: 0 !important;
  border-right: 0 !important;
}

.ideas-container {
  padding: 8px;
  max-width: none;
  width: 100%;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  height: 100%;
  align-items: flex-start;
}

.ideas {
  margin: 8px 0;
  line-height: 1.6;
  color: rgba(0, 0, 0, 0.87);
  align-items: flex-start;
  display: flex;
  flex-direction: column;
  width: 100%;
}

.v-expansion-panel-text {
  padding: 16px;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: flex-start;
}

/* Enforce top alignment for all table cells and their direct children */
:deep(.v-data-table__td),
:deep(.v-data-table__th),
:deep(.v-data-table__td) > *,
:deep(.v-data-table__th) > *,
:deep(.v-data-table__tr) {
  vertical-align: top !important;
  align-items: flex-start !important;
  justify-content: flex-start !important;
  border-left: 0 !important;
  border-right: 0 !important;
}

/* Vuetify wraps cell content in a flex container; force top alignment there too */
:deep(.v-data-table__td .v-data-table__td-content),
:deep(.v-data-table__th .v-data-table__th-content) {
  align-items: flex-start !important;
  justify-content: flex-start !important;
  display: flex !important;
  flex-direction: column !important;
}

/* Force header text to be top-left aligned */
:deep(.v-data-table__th) {
  text-align: left !important;
  vertical-align: top !important;
}
:deep(.v-data-table-header__content) {
  justify-content: flex-start !important;
  align-items: flex-start !important;
}

/* Allow rows to grow naturally when Ideas expands */
:deep(.v-data-table__tr) {
  height: auto !important;
}

/* Center the whole table content within the card while allowing tall rows.
   The cells remain top-aligned due to the rules above. */
:deep(.v-data-table__wrapper table) {
  margin-left: auto;
  margin-right: auto;
}

.measures-container {
  padding: 0 !important;
  width: 100% !important;
  max-width: 100% !important;
  border: 0 !important;
  border-left: 0 !important;
  border-right: 0 !important;
}
.measures-container * {
  border-left: 0 !important;
  border-right: 0 !important;
  max-width: 100% !important;
}

.task-input {
  margin-bottom: 12px !important;
  width: 100% !important;
  max-width: 100% !important;
}

.task-input :deep(.v-field__append-inner) {
  padding-top: 6px;
}

.task-input :deep(.v-field__input) {
  min-height: 32px;
  padding-top: 0;
  padding-bottom: 0;
}

.task-list {
  display: flex;
  flex-direction: column;
  gap: 0;
  width: 100% !important;
  max-width: 100% !important;
  border: 0 !important;
  border-left: 0 !important;
  border-right: 0 !important;
  border-top: 0 !important;
  padding: 0 !important;
  margin: 0 !important;
}

.task-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  width: 100% !important;
  max-width: 100% !important;
  min-height: 32px;
  padding: 8px 0 !important;
  margin: 0 !important;
  border-radius: 0;
  border: 0 !important;
  border-top: 0 !important;
  border-bottom: 1px solid rgba(0, 0, 0, 0.12) !important; /* horizontal delimiter only */
  border-left: 0 !important;
  border-right: 0 !important;
  background: transparent;
  box-sizing: border-box !important;
}
.task-item:last-child {
  border-bottom: 0 !important; /* Remove border from last item */
}
.task-item:first-child {
  border-top: 0 !important;
  padding-top: 0 !important;
}

.task-summary {
  flex: 1 1 auto;
  margin-right: 8px;
  white-space: normal;
  word-break: break-word;
  font-size: 0.875rem;
  color: rgba(0, 0, 0, 0.87);
  line-height: 1.25;
  max-width: 100% !important;
  width: 100%;
}

.task-item:hover {
  background: rgba(0, 0, 0, 0.04);
}

.delete-icon {
  opacity: 0.6;
  cursor: pointer;
  align-self: flex-start;
}

.delete-icon:hover {
  opacity: 1;
}

.v-expansion-panels {
  background: transparent;
}

.v-expansion-panel {
  margin-bottom: 4px;
  border: 1px solid var(--v-border-color);
  border-radius: 4px !important;
  background: var(--v-background-base);
}

.v-expansion-panel-title {
  padding: 12px 16px;
  min-height: 48px;
}

.gap-2 {
  gap: 8px;
}

.text-h5 {
  font-size: 1.25rem !important;
  font-weight: 500 !important;
  letter-spacing: 0.0125em !important;
}
</style>
